// features/findings/quebec-quiet-revolution.js
// ════════════════════════════════════════════════════════════════════
// LAB FINDING — Quebec's Quiet Revolution: A Counterfactual Walk (v1)
// Form 5 — Counterfactual walk. Pre-registered 2026-05-08.
//
// The eighth popdec finding. Mirror case to Korea's Lock-In (slow decay)
// applied to the steepest documented OECD condition-decay → fertility-
// decline sequence. Quebec TFR fell ~4.0 (1955) → ~1.5 (1990).
// Quebec is also the most informative policy-without-conditions
// failure case: 5% of GDP family policy 1988-2024, no cohort recovery.
// ════════════════════════════════════════════════════════════════════

import {
  FindingBack,
  FindingHeader,
  FindingSection,
  FindingSubhead,
  FindingPill,
  FindingPillRow,
} from './components-shared';
import {
  DivergenceTimeline,
  PredictionsBlock,
  MagnitudeBars,
} from './charts';

// ─── Chart data ─────────────────────────────────────────────────────

// Quebec TFR 1950-2024 with comparison: France (gradualist Catholic
// secularization) and Korea (slow-decay analog).
// Sources: ISQ (Institut de la statistique du Québec), INED France,
// KOSIS Korea.
const QUEBEC_TRAJECTORIES = [
  {
    id: 'quebec',
    label: 'Quebec',
    color: 'oxblood',
    points: [
      { x: 1955, y: 4.00 },
      { x: 1960, y: 3.85 },
      { x: 1965, y: 3.10 },
      { x: 1970, y: 2.10 },
      { x: 1975, y: 1.75 },
      { x: 1985, y: 1.45 },
      { x: 1990, y: 1.50 },
      { x: 2000, y: 1.45 },
      { x: 2009, y: 1.74 },
      { x: 2015, y: 1.60 },
      { x: 2024, y: 1.49 },
    ],
  },
  {
    id: 'france',
    label: 'France (gradualist comparator)',
    color: 'sage',
    points: [
      { x: 1955, y: 2.70 },
      { x: 1965, y: 2.85 },
      { x: 1975, y: 1.95 },
      { x: 1985, y: 1.81 },
      { x: 1995, y: 1.71 },
      { x: 2005, y: 1.94 },
      { x: 2015, y: 1.96 },
      { x: 2024, y: 1.66 },
    ],
  },
  {
    id: 'korea',
    label: 'Korea (slow-decay analog)',
    color: 'cognac',
    points: [
      { x: 1990, y: 1.57 },
      { x: 2000, y: 1.45 },
      { x: 2010, y: 1.23 },
      { x: 2024, y: 0.75 },
    ],
  },
];

// Branch-point magnitude ranking — six branches with TFR-effect
// magnitudes (mechanism-dossier estimates).
const BRANCH_RANKING = [
  {
    label: 'B2 — 1964-65 nationalization (LEVER, largest)',
    value: 0.50,
    tone: 'oxblood',
  },
  {
    label: 'B1 — 1960 Quiet Revolution mandate (LEVER)',
    value: 0.30,
    tone: 'oxblood',
  },
  {
    label: 'B3 — 1968-69 contraception + divorce (PARTIAL LEVER)',
    value: 0.20,
    tone: 'cognac',
  },
  {
    label: 'B4 — 1970-75 secular-marriage shift (LOCKING)',
    value: 0.10,
    tone: 'plum',
  },
  {
    label: 'B5 — 1980s cohort completes sub-replacement (LOCKING)',
    value: 0.08,
    tone: 'plum',
  },
  {
    label: 'B6 — 1988 baby-bonus + 1997 daycare (LOCKING)',
    value: 0.05,
    tone: 'plum',
  },
];

// Pre-1960 vs current condition state — Quebec's 7/7 → 1.5/7 collapse.
const QUEBEC_CONDITION_TRAJECTORY = [
  { label: 'Pre-1960 — 7/7 conditions, TFR 4.0',          value: 7.0, tone: 'sage' },
  { label: '1965 — 5/7 post-nationalization, TFR 3.10',   value: 5.0, tone: 'forest-moss' },
  { label: '1975 — 2.5/7 mid-decay, TFR 1.75',            value: 2.5, tone: 'cognac' },
  { label: '1990 — 1.5/7 post-decay plateau, TFR 1.50',   value: 1.5, tone: 'oxblood' },
  { label: '2024 — 1.5/7 post-policy spend, TFR 1.49',    value: 1.5, tone: 'oxblood' },
];

// ─── Predictions ────────────────────────────────────────────────────

export const QUEBEC_PREDICTIONS = [
  {
    label: 'P1 — Quebec TFR 2027 in [1.40, 1.65]',
    central: 1.50,
    band: [1.40, 1.65],
    falsifierBand: [1.30, 1.75],
    resolutionDate: '2028 ISQ release',
    status: 'open',
    note: 'Tests whether the 2009-2024 reversion continues or whether Quebec finds another tempo lift. Framework predicts plateau in 1.4-1.6 band absent condition reformation.',
  },
  {
    label: 'P2 — Quebec cohort 1990-1995 completes in [1.40, 1.65]',
    central: 1.55,
    band: [1.40, 1.65],
    falsifierBand: [1.30, 1.80],
    resolutionDate: '2030+ ISQ cohort completion data',
    status: 'open',
    note: 'Cohort completion test — distinguishes tempo from cohort. The 2002-2009 period TFR lift was tempo; cohort fertility for women born 1990-95 should land in the 1.4-1.6 band per the framework. Falsified if cohort >1.80 (genuine recovery) or <1.30 (worse decline than projected).',
  },
  {
    label: 'P3 — Quebec\'s 5%-GDP family policy produces no sustained cohort recovery to ≥2.0 by 2040',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2040 ISQ + Statistics Canada cohort completion',
    status: 'open',
    note: 'Single-falsifier-suffices. Quebec is the most informative policy-without-conditions test case (subnational government, sustained 5% GDP, 36-year window, no external mobilization confound). If the static-negative thesis fails anywhere, it should fail in Quebec. It hasn\'t through 2024.',
  },
  {
    label: 'P4 — Anti-prediction: zero rapid-secularization Catholic-tradition jurisdictions recover to ≥2.0 sustained 5+ years by 2040',
    central: 0,
    band: [0, 0],
    falsifierBand: [0, 1],
    resolutionDate: '2040 cumulative national-statistics review',
    status: 'open',
    note: 'Cross-case generalization. Quebec, Italy, Spain, Ireland, Catholic-Bavaria all underwent rapid post-1960 secularization at varying speeds. Framework predicts none reach sustained TFR ≥2.0 by 2040.',
  },
  {
    label: 'P5 — Rural Catholic-traditional vs urban secular Quebec sub-population gap stable or widening',
    central: 8,
    band: [-5, 20],
    falsifierBand: [-30, 40],
    resolutionDate: '2030+ ISQ regional cohort data',
    status: 'open',
    note: 'Tests within-Quebec sub-population divergence per framework prediction. Two-tailed falsifier: gap closing 30%+ (conditions don\'t matter within Quebec) OR widening beyond 40% (selection effects dominate over conditions).',
  },
];

// ─── Finding metadata ───────────────────────────────────────────────

export const FINDING_META = {
  slug: 'quebec-quiet-revolution',
  domain: 'popdec',
  year: 2026,
  version: 'v1',
  lifecycle: 'pre-registered',
  prevVersion: null,
  revisedBy: null,
  whatChanged: null,
  retractionNote: null,
  citationHandle: 'lab:finding/popdec/2026/quebec-quiet-revolution/v1',
  title: "Quebec's Quiet Revolution: A Counterfactual Walk Through the OECD's Steepest Documented Condition-Decay Sequence",
  shortTitle: "Quebec's Quiet Revolution",
  pageTitle: "Quebec's Quiet Revolution — Lab Finding · Shift",
  description:
    'Form 5 counterfactual walk applied to Quebec, the steepest documented OECD condition-decay → fertility-decline sequence (TFR 4.0 in 1955 → 1.5 in 1990). Six branch points 1960-1988. Three levers (1960 election; 1965 nationalization; 1968-69 contraception/divorce). Three lockings (secular-marriage normalization; cohort completion; baby-bonus + daycare introduced post-decay). Quebec is also the most informative policy-without-conditions failure case — 5% GDP family policy 1988-2024, no sustained cohort recovery. Five pre-registered predictions resolving 2028-2040.',
  form: 5,
  formName: 'Counterfactual walk (rapid-decay variant)',
  reflexivity: 'MEDIUM-HIGH',
  epistemicStatus: 'mechanism',
  confidence: 'medium',
  sampleSize: 1,
  preregisteredAt: '2026-05-08',
  visibility: 'public-peek',
};

// ─── Article ────────────────────────────────────────────────────────

export default function QuebecQuietRevolutionFinding() {
  return (
    <article className="finding-article">
      <FindingBack href="/findings" label="← Findings index" />

      <FindingHeader
        eyebrow="§ Lab finding · popdec/2026/quebec-quiet-revolution/v1"
        title={FINDING_META.title}
        sub="The eighth popdec finding. Rapid-decay mirror to Korea's Lock-In (Form 5)."
        subItalic="Quebec's TFR fell from ~4.0 (1955) to ~1.5 (1990) — 2.5 children per woman in 35 years, the steepest documented in any OECD jurisdiction. The collapse tracked, condition-by-condition, the Quiet Revolution's deliberate dismantling of Catholic-Church institutional control. Six branch points: three levers, three lockings. Plus 36 years (1988-2024) of $5%-of-GDP family policy that produced tempo at best, never cohort recovery. The static-negative thesis confirmed at a different scale."
        meta={FINDING_META}
      />

      <section className="finding-lede">
        <p>
          From the inside, Quebec's TFR collapse 1955-1990 looks like one continuous descent. From a counterfactual perspective, it has six clear branch points where different decisions or different conditions might have produced a different trajectory. Tracing them carefully reveals: <strong>the levers — moments where intervention would have mattered — closed before the decline became visible.</strong> By the time Quebec's TFR fell below replacement in 1971, the structural conditions had already been dismantled (1965 nationalization of education and healthcare). By 1988, when Quebec began aggressive family-policy spending, the cohorts who would carry the recovery had already lived through the condition-decay window. Quebec's $5%-GDP family policy (1988-2024) has produced one tempo lift (2002-2009, TFR 1.49 → 1.74) that fully reverted (1.49 by 2024). The framework predicts: post-condition-decay, policy compensates at margins, cannot reproduce the conditions. Quebec confirms this at the most informative scale available — subnational government, sustained spending, 36-year window, no external-mobilization confound.
        </p>
      </section>

      <section className="finding-tldr">
        <div className="finding-tldr-eyebrow">TL;DR</div>
        <p className="finding-tldr-verdict">
          <strong>Quebec is the OECD's steepest documented condition-decay → fertility-decline sequence.</strong> Six branch points 1960-1988 caused the collapse: 1960 Liberal Party mandate (lever), 1964-65 nationalization of education and healthcare (lever, largest in the corpus), 1968-69 contraception/divorce liberalization (partial lever), 1970-75 secular-marriage normalization (locking), 1980s cohort completion at sub-replacement (locking), 1988 baby-bonus + 1997 daycare (locking — policy-without-conditions failure). Cumulative counterfactual: had Quebec pursued gradual reform-within-Catholic-frame, projected 2024 TFR ~1.8-2.0 (France-like), not actual 1.49. Quebec's $5%-GDP family policy 1988-2024 has produced tempo, never cohort recovery. The static-negative thesis is confirmed.
        </p>
        <FindingPillRow>
          <FindingPill tone="mechanism">mechanism documented</FindingPill>
          <FindingPill tone="warn">Confidence: medium</FindingPill>
          <FindingPill tone="neutral">N=1 trajectory · 6 branch points</FindingPill>
          <FindingPill tone="neutral">Pre-registered 2026-05-08</FindingPill>
          <FindingPill tone="failure">Reflexivity: MEDIUM-HIGH</FindingPill>
        </FindingPillRow>
        <div className="finding-tldr-corpus">
          <strong>Corpus:</strong> Quebec primary; France (gradualist comparator) + Korea (slow-decay analog) for cross-validation. Sources: ISQ (Institut de la statistique du Québec), Statistics Canada, INED France, KOSIS Korea, Human Fertility Database. Cross-cite: <code>lab:finding/popdec/2026/korea-lockin/v1</code> (slow-decay Form 5 analog), <code>lab:finding/popdec/2026/korea-hungary-divergence/v1</code> (static-negative thesis Quebec independently confirms).
        </div>
      </section>

      {/* ── §1 The trajectory ── */}
      <FindingSection
        eyebrow="§1 The trajectory"
        title="Quebec's collapse, France's gradualism, Korea's slower decay."
      >
        <p className="finding-prose">
          Three trajectories in one chart: Quebec (rapid Catholic dismantling 1960-1975), France (gradualist Catholic-secular accommodation), Korea (slow-decay 1995-2024). The framework predicts each consistent with its specific structural-conditions trajectory.
        </p>

        <DivergenceTimeline
          title="Quebec, France, Korea TFR trajectories. Quebec's 4.0→1.5 collapse 1955-1990 is the steepest peacetime OECD decline. France's gradualist trajectory holds 1.65-1.95 across 1985-2015. Korea's slow decay (1995-2024) collapses to 0.75 — sharper trough than Quebec's plateau."
          series={QUEBEC_TRAJECTORIES}
          xLabel="Year"
          yLabel="TFR"
          yMin={0}
          yMax={4.5}
        />

        <p className="finding-prose">
          Three observations:
        </p>
        <ul className="finding-bullets">
          <li><strong>Quebec collapsed faster than Korea.</strong> Quebec lost 2.5 TFR in 35 years (1955-1990); Korea lost ~1.0 TFR in the comparable 1990-2024 window. Quebec's collapse is the steeper one because the condition-dismantling was state-driven and explicit, not market-driven and gradual.</li>
          <li><strong>France held above Quebec throughout.</strong> France's gradualist Catholic-secular accommodation produced ~0.3 TFR difference from Quebec across 1975-2010. The framework predicts: gradual condition decay produces gradual TFR decline; rapid decay produces rapid decline.</li>
          <li><strong>Quebec's 2002-2009 lift is the policy-tempo signature.</strong> $5/day daycare introduced 1997; expanded coverage 2000-2009 produced a tempo bounce 1.49 → 1.74. Reversion 2010-2024 to 1.49 is the framework's prediction (tempo without cohort).</li>
        </ul>
      </FindingSection>

      {/* ── §2 The pre-1960 baseline ── */}
      <FindingSection
        eyebrow="§2 The pre-1960 baseline"
        title="Quebec at 7/7 conditions: revanche des berceaux."
      >
        <p className="finding-prose">
          Pre-1960 Quebec was a documented case of a committed-religious-majority country at population scale. The Catholic Church controlled education, healthcare, social services, marriage, charitable institutions, and provided the unitary identity narrative ("Catholic-French-Canadian-against-English-Protestant") that distinguished Quebec from English Canada.
        </p>

        <p className="finding-prose">
          Pre-1960 Quebec held all seven structural conditions:
        </p>

        <ul className="finding-bullets">
          <li><strong>C1 — Religious-community density:</strong> PRESENT (~95% Catholic adherence; church-centered communities)</li>
          <li><strong>C2 — Multi-generational household economics:</strong> PRESENT (rural farm structure + urban Catholic-network housing patterns)</li>
          <li><strong>C3 — Alternative-status structure:</strong> PRESENT (priesthood + religious orders provided status currency decoupled from professional achievement; large families conferred specific Catholic-civic status — the "revanche des berceaux" was an explicit demographic strategy)</li>
          <li><strong>C4 — Kinship-network childcare:</strong> PRESENT (extended family + parish networks distributed childcare)</li>
          <li><strong>C5 — Identity-narrative coherence:</strong> PRESENT (Catholic-Quebec identity; large families framed as nation-building)</li>
          <li><strong>C6 — Marriage-formation infrastructure:</strong> PRESENT (Catholic marriage age young; strong social pressure; Catholic dating networks)</li>
          <li><strong>C7 — Exit cost:</strong> PRESENT (leaving Catholic identity carried real social, family, employment cost)</li>
        </ul>

        <p className="finding-prose">
          7/7 conditions at population scale (mass-cultural-conformity rather than committed-minority structure). The framework predicts TFR in the 4.0-6.5 band; Quebec's actual 4.0 sits at the lower end (consistent with mass-conformity vs committed-minority distinction — Haredi at 7/7 produces 6.5; Quebec at 7/7 produced 4.0).
        </p>
      </FindingSection>

      {/* ── §3 The six branch points ── */}
      <FindingSection
        eyebrow="§3 The six branch points"
        title="Three levers, three lockings."
      >
        <p className="finding-prose">
          Six branch points 1960-1988 marked Quebec's transition from 7/7 conditions to 1.5/7. The first three are levers — counterfactual paths existed; different decisions or different timing might have preserved more conditions. The last three are lockings — by the time they arrived, the structural decay was already embedded.
        </p>

        <FindingSubhead>Branch 1 — 1960 Liberal Party / Quiet Revolution mandate (LEVER)</FindingSubhead>
        <p className="finding-prose">
          The Liberal Party's 1960 election victory under "Maîtres chez nous" — explicitly about replacing Catholic-Church institutional control with secular state control. Margin of victory was ~5 percentage points. <strong>Counterfactual:</strong> Union Nationale victory would have delayed the Quiet Revolution; gradual reform within Catholic frame would have preserved ~0.30 TFR cumulative.
        </p>

        <FindingSubhead>Branch 2 — 1964-65 nationalization of education + healthcare (LEVER, largest)</FindingSubhead>
        <p className="finding-prose">
          Bill 16 (1964) reformed civil law; 1965 nationalized education and healthcare from the Catholic Church. The Department of Education replaced 200+ years of Catholic operational control. <strong>This was the move that dismantled C1 + C3 + C5 simultaneously.</strong> Counterfactual: gradual reform within Catholic frame (parallel to Bavarian/Italian/Spanish-Catholic regions where secular state grew alongside Catholic structures) would have preserved ~0.50 TFR cumulative — the largest single branch in the Quebec sequence.
        </p>

        <FindingSubhead>Branch 3 — 1968-69 federal contraception + divorce liberalization (PARTIAL LEVER)</FindingSubhead>
        <p className="finding-prose">
          Federal Bill C-150 (Trudeau "omnibus bill") legalized contraception, decriminalized abortion in some circumstances, liberalized divorce. Quebec adopted faster than other provinces because Catholic-Church social authority had already been weakened by the 1964-65 changes. Counterfactual: had Catholic-Church retained social authority over marriage and contraception (as Italy did until 1971 divorce, 1978 abortion), C5 + C7 would have decayed more slowly. ~0.20 TFR.
        </p>

        <FindingSubhead>Branch 4 — 1970-1975 secular-marriage normalization (LOCKING)</FindingSubhead>
        <p className="finding-prose">
          By 1975, civil marriage had replaced Catholic marriage as the modal form. Marriage age rose from ~22 (1955) to ~26 (1975). Cohabitation began rising. <strong>This is a LOCKING because no realistic counterfactual remained — the 1965 nationalization had removed the Church from education; the 1968-69 federal changes had removed it from marriage and contraception.</strong> ~0.10 TFR magnitude.
        </p>

        <FindingSubhead>Branch 5 — 1980s cohort completion at sub-replacement (LOCKING)</FindingSubhead>
        <p className="finding-prose">
          Women born 1955-65 (the first post-Quiet-Revolution cohort) completed reproduction at TFR 1.5-1.7. The rapid 1965-1985 decline was the cohort effect of the 1965-1969 condition collapse. <strong>By the time this branch arrived, the cohort had already lived through the transition; no counterfactual existed at this branch.</strong>
        </p>

        <FindingSubhead>Branch 6 — 1988 baby-bonus + 1997 $5/day daycare (LOCKING — policy-without-conditions)</FindingSubhead>
        <p className="finding-prose">
          Quebec began major family-policy spending 1988: $500/$1500/$7500 baby bonus by parity. 1997 introduced $5/day daycare (later $7/day), one of the OECD's most aggressive family-policy interventions. Total spending reached ~5% GDP by 2010s. <strong>Counterfactual:</strong> could these interventions have produced sustained recovery? Framework predicts no — and data confirms. Quebec TFR rose modestly 2002-2009 (1.49 → 1.74) before declining again to 1.49 by 2024. The 2002-2009 lift was tempo (delayed births moved earlier under daycare-policy incentives), not cohort recovery. Cohort fertility for women born 1980-1990 projected at 1.5-1.7.
        </p>

        <FindingSubhead>Branch ranking — magnitude</FindingSubhead>
        <p className="finding-prose">
          Sorted by counterfactual TFR magnitude. The largest is Branch 2 (1964-65 nationalization, ~0.50 TFR cumulative). The lockings (Branches 4-6) are minor compared to the levers (Branches 1-3) — counterfactual leverage closed early.
        </p>

        <MagnitudeBars
          title="Branch-point magnitude (cumulative TFR effect, mechanism-dossier estimates). Oxblood = LEVER (counterfactual existed). Cognac = PARTIAL LEVER. Plum = LOCKING (downstream of earlier branches). The 1964-65 nationalization is the single largest branch in the Quebec sequence."
          items={BRANCH_RANKING}
          valueLabel="TFR effect"
        />
      </FindingSection>

      {/* ── §4 The condition-trajectory ── */}
      <FindingSection
        eyebrow="§4 The condition-trajectory"
        title="From 7/7 to 1.5/7 in 30 years; then plateau."
      >
        <p className="finding-prose">
          Quebec's structural-conditions count fell from 7/7 (pre-1960) to 1.5/7 (1990) over 30 years. Since 1990, the count has remained at ~1.5/7 — policy spending has prevented further condition loss but has not restored conditions.
        </p>

        <MagnitudeBars
          title="Quebec's condition count over time, with concurrent TFR [counts are mechanism-dossier estimates from per-cell judgments; ±0.5 sensitivity]. The 1965-1990 window is the rapid decay; 1990-2024 is the plateau. Sage = pre-decay. Forest-moss = transitional. Cognac = mid-decay. Oxblood = post-decay plateau."
          items={QUEBEC_CONDITION_TRAJECTORY}
          valueLabel="condition count (estimated)"
        />

        <p className="finding-prose">
          The cumulative counterfactual: had Quebec pursued gradual reform-within-Catholic-frame rather than rapid secularization, the projected condition count at 2024 would be ~3-4/7 (France-like) rather than 1.5/7. Projected 2024 TFR would be ~1.8-2.0, not the actual 1.49. Cumulative magnitude ~0.5-0.8 TFR over 50 years.
        </p>
      </FindingSection>

      {/* ── §5 The policy-without-conditions failure ── */}
      <FindingSection
        eyebrow="§5 The policy-without-conditions failure"
        title="$5%-GDP, 36 years, no sustained cohort recovery."
      >
        <p className="finding-prose">
          Quebec's family-policy spending 1988-2024 is the most informative test case for the policy-without-conditions thesis (Korea-Hungary's static-negative claim). Five reasons:
        </p>
        <ul className="finding-bullets">
          <li><strong>Subnational government with constitutional authority over family policy.</strong> Quebec controls its own family policy independently of federal Canada; the test is clean.</li>
          <li><strong>Substantial sustained spending.</strong> ~5% GDP, comparable to Hungary's 6.2%.</li>
          <li><strong>36-year window (1988-2024).</strong> Long enough that cohort effects are visible.</li>
          <li><strong>No external mobilization context.</strong> Unlike Israel (high-mobilization elevates baseline), Quebec has no comparable confound.</li>
          <li><strong>French-Canadian cultural specificity is well-bounded.</strong> Quebec has no comparable refugia-style positive control within itself; no within-case confound.</li>
        </ul>
        <p className="finding-prose">
          Result: <strong>tempo lift 2002-2009 (1.49 → 1.74), full reversion 2010-2024 (1.74 → 1.49). Cohort fertility for women born 1980-1990 projected at 1.5-1.7 (no meaningful cohort gain).</strong> If the static-negative thesis fails anywhere, it should fail in Quebec. It hasn't.
        </p>
        <p className="finding-prose">
          A weaker reading: Quebec's $5%-GDP spending may have prevented further decline (TFR could have collapsed to 1.2-1.3 Hungary-like without the daycare program). The framework's prediction is that policy compensates at margins; preventing further decline is a margin compensation. Quebec's experience is consistent with this moderate reading: some margin compensation, no cohort recovery.
        </p>
      </FindingSection>

      {/* ── §6 Pre-registered predictions ── */}
      <FindingSection
        eyebrow="§6 Pre-registered predictions"
        title="Five predictions. Resolution 2028-2040."
      >
        <p className="finding-prose">
          The framework's retrospective explanation of Quebec holds; forward predictions test whether continued condition-stability + policy-spending continues to produce the same trajectory.
        </p>

        <PredictionsBlock
          title="Pre-registered predictions — Quebec trajectory continuation, cohort confirmation, policy-failure anti-prediction, cross-case generalization, sub-population gap"
          predictions={QUEBEC_PREDICTIONS}
        />

        <p className="finding-prose">
          P3 stakes the framework on Quebec's $5%-GDP policy continuing to fail at producing sustained cohort recovery. P4 generalizes across rapid-secularization Catholic-tradition jurisdictions (Italy, Spain, Ireland, Catholic-Bavaria). Both are single-falsifier-suffices designs.
        </p>
      </FindingSection>

      {/* ── §7 Caveats ── */}
      <FindingSection
        eyebrow="§7 Honest accounting"
        title="First-class caveats. Read before citing."
      >
        <FindingSubhead>The "rapid vs gradual" attribution problem</FindingSubhead>
        <p className="finding-prose">
          The cumulative counterfactual (~0.5-0.8 TFR over 50 years had reform been gradual) is sensitive to the choice of comparison case. France pursued gradualist Catholic-secular accommodation (TFR 1.65-1.95 trajectory). Italy pursued slower secularization than Quebec (TFR 1.20-1.30 trajectory). Choosing France as the gradualist comparator produces the larger counterfactual; choosing Italy produces a smaller one. The framework's claim is <em>probabilistic</em>, not deterministic.
        </p>

        <FindingSubhead>Cultural-specificity confound</FindingSubhead>
        <p className="finding-prose">
          Quebec's pre-1960 high TFR was specifically Catholic-French-Canadian-nationalist (the "revanche des berceaux"). The cultural frame was about demographic survival against English-Canadian assimilation pressure. This added a national-mobilization context that other Catholic-tradition jurisdictions lacked. The framework's "Catholic-tradition" generalization (Italy, Spain, Ireland, Bavaria) under-weights this Quebec-specific factor.
        </p>

        <FindingSubhead>Tempo vs cohort attribution at the 2002-2009 lift</FindingSubhead>
        <p className="finding-prose">
          The 2002-2009 TFR lift (1.49 → 1.74) is interpretable as tempo (delayed births moved earlier under daycare incentives) rather than cohort recovery. Cohort completion data for women born 1975-85 supports this interpretation but not definitively. v2 will integrate completed cohort data once available.
        </p>

        <FindingSubhead>Policy-effectiveness null is strong but not absolute</FindingSubhead>
        <p className="finding-prose">
          The framework's claim is that Quebec's $5%-GDP didn't produce sustained cohort recovery. A weaker reading is that the spending may have <em>prevented further decline</em>. The framework's prediction is that policy compensates at margins; preventing further decline is a margin compensation. The strong reading (no effect at all) is not what the framework claims; the moderate reading is consistent with the data.
        </p>

        <FindingSubhead>Quebec's 2009-2024 reversion is informative beyond Quebec</FindingSubhead>
        <p className="finding-prose">
          The 2009-2024 reversion (TFR 1.74 → 1.49) provides independent confirmation of the tempo-vs-cohort interpretation introduced in Korea-Hungary. The reversion happens within a single national-policy regime; it cannot be attributed to policy retreat. Consistent with tempo-completion: available delayed births moved forward; once that catch-up saturated, TFR returned to underlying cohort trajectory.
        </p>

        <FindingSubhead>Reflexivity: MEDIUM-HIGH</FindingSubhead>
        <p className="finding-prose">
          A widely-cited finding that "Quebec's $5%-GDP family policy didn't work" can produce policy retreat (defunding daycare programs) or political backlash. The finding's claim is descriptive — Quebec's experience is informative about the limits of policy when conditions have decayed — not prescriptive about what policy should do in 2026 forward. <strong>Framed as diagnostic + structural-condition-inventory, not policy advice.</strong>
        </p>
      </FindingSection>

      {/* ── §8 Cross-references and what's next ── */}
      <FindingSection
        eyebrow="§8 What this means for the next question"
        title="The popdec arc deepens with rapid-decay Form 5."
      >
        <p className="finding-prose">
          This finding is the rapid-decay mirror to <code>lab:finding/popdec/2026/korea-lockin/v1</code>. Korea Lock-In documented slow Korean decay 1997-2024; this finding documents rapid Quebec decay 1960-1990. Both demonstrate the framework's directionality: condition-decay → fertility-decline; the speed of decline tracks the speed of decay.
        </p>

        <FindingSubhead>Open questions for v9+</FindingSubhead>
        <ul className="finding-bullets">
          <li><strong>Italy / Spain / Ireland comparative.</strong> P4 names multiple cases. A future Form 4 (Pattern across cases) finding could systematically apply the seven-condition framework to all post-1960 rapid-secularization Catholic-tradition cases. Quebec, Italy, Spain, Ireland, Catholic-Bavaria should produce similar trajectories at varying speeds; deviations are informative.</li>
          <li><strong>The "gradualist comparator" question.</strong> France's gradualist Catholic-secular accommodation produced TFR 1.65-1.95. What specifically about France's structural-conditions trajectory differs from Quebec's? A targeted Form 2 (Pair comparison) finding (Quebec vs France) could isolate the gradualism-vs-rapidity variable.</li>
          <li><strong>The "policy-effectiveness margin" specification.</strong> Quebec's 2002-2009 modest tempo lift quantifies one margin. A future finding could specify what tempo magnitudes are achievable for what spending levels — useful for cost-benefit framing of family-policy investment even when sustained recovery is impossible.</li>
          <li><strong>Within-Quebec sub-population analysis.</strong> P5 predicts rural Catholic-traditional vs urban secular gap continues. A within-Quebec stratification study could produce data analogous to Israel's Haredi-secular split.</li>
        </ul>

        <FindingSubhead>Cross-references</FindingSubhead>
        <p className="finding-prose">
          This finding extends the popdec arc with a rapid-decay Form 5 case. Read together with the other seven findings, the corpus now has substantial coverage of:
        </p>
        <ul className="finding-bullets">
          <li>Static comparison (<code>korea-hungary-divergence</code>)</li>
          <li>Slow-decay counterfactual (<code>korea-lockin</code>)</li>
          <li>Forward falsifier (<code>positive-case-search</code>)</li>
          <li>Single-case structural positive (<code>israel-haredi-floor</code>)</li>
          <li>Cross-case structural positive (<code>refugia-template</code>)</li>
          <li>Cross-substrate (<code>substrate-symmetric-refugia</code>)</li>
          <li>Historical retrospective (<code>baby-boom-detective</code>)</li>
          <li>Rapid-decay counterfactual (<code>quebec-quiet-revolution</code> — this finding)</li>
        </ul>
        <p className="finding-prose">
          47 pre-registered predictions across the eight findings, resolution windows 2027-2050.
        </p>
      </FindingSection>

      <footer className="finding-footer">
        <div className="finding-footer-eyebrow">Footer ledger</div>
        <dl className="finding-meta">
          <dt>Methodology Version</dt><dd>Lab V2.0</dd>
          <dt>Form</dt><dd>Form 5 — Counterfactual walk (rapid-decay variant)</dd>
          <dt>Reflexivity</dt><dd>MEDIUM-HIGH — findings about specific jurisdictions' policy effectiveness can produce policy retreat or political backlash</dd>
          <dt>Epistemic status</dt><dd>Mechanism documented + counterfactual quantified</dd>
          <dt>Sample size</dt><dd>N=1 trajectory; 6 branch points within</dd>
          <dt>Pre-registration date</dt><dd>2026-05-08. Resolution: 2028 (P1), 2030+ (P2, P5), 2040 (P3, P4)</dd>
          <dt>Visibility</dt><dd>Public · read-only</dd>
          <dt>Citation handle</dt><dd><code>{FINDING_META.citationHandle}</code></dd>
          <dt>Falsifier</dt><dd>Any of: P1 (Quebec TFR 2027 outside [1.30, 1.75]); P2 (cohort completion outside [1.30, 1.80]); P3 (any 5+ year sustained TFR ≥2.0 with cohort confirmation by 2040); P4 (any rapid-secularization Catholic-tradition jurisdiction recovers to sustained ≥2.0 by 2040); P5 (rural-urban gap closes 30%+ or rural converges with urban).</dd>
        </dl>
        <div className="finding-citation">
          <strong>Cite as:</strong> Mai, T. (2026). Quebec's Quiet Revolution: A Counterfactual Walk Through the OECD's Steepest Documented Condition-Decay Sequence. Lab Finding {FINDING_META.citationHandle}.
        </div>
        <div className="finding-version">
          <div className="finding-footer-eyebrow">Update history</div>
          <p>v1 — 2026-05-08 — initial pre-registration. Resolution windows: 2028, 2030+, 2040.</p>
        </div>
      </footer>
    </article>
  );
}
